import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import RNMapView, { Region } from 'react-native-maps'
import { useIsFocused } from '@react-navigation/native'
import { RegionType, TokenType } from '@vatom/sdk/core'
import { MapBoundingBox, useConfig, useGeoDiscoveryGroups, useSDK } from '@vatom/sdk/react'
import { VatomTokenType } from '@vatom/vatom/plugin'
import * as Location from 'expo-location'
import { LocationAccuracy } from 'expo-location'
import { useThrottledCallback } from 'use-debounce'

import { useBusinessSelector } from '../../../hooks/useBusinessSelector'
import { useCurrentLocation } from '../../../hooks/useCurrentLocation'
import { getBoundingBoxFromRegion } from '../../../utils/map'

import {
  createCustomMarkers,
  createGroupMarkers,
  createSingleMarkers,
  createUserLocationMarkers
} from './createMarkers'
import { useMapActions } from './useMapActions'

const locationOptions: Location.LocationOptions = {
  accuracy: LocationAccuracy.Balanced,
  distanceInterval: 5
}

// Map zoom 0-20
const PRECISION_MIN = 2
// const PRECISION_MAX = 14
const PRECISION_ZOOM_SUBTRACT = 6
const PRECISION_DEFAULT = 7

function getPrecision(latitudeDelta: number) {
  const zoomLevel = Math.round(Math.log(360 / latitudeDelta) / Math.LN2)
  // The object here is the zoomLeveL: precision. Some zoomLevel needed specific precision in order to look good in the map
  const zoomPrecision = { 9: 5, 8: 4, 7: 4, 6: 3, 5: 3 } as Record<number, number>
  const zoomPrecisionMagic = Number(zoomLevel - PRECISION_ZOOM_SUBTRACT)
  const precision = Math.max(zoomPrecision[zoomLevel] ?? zoomPrecisionMagic, PRECISION_MIN)
  return precision
}

export const useMaps = ({
  mapRef
}: {
  mapRef?: MutableRefObject<RNMapView> | MutableRefObject<google.maps.Map>
} = {}) => {
  const sdk = useSDK()
  const pickingRef = useRef(false)
  const [lastPicked, setLastPicked] = useState<string>()
  const removeLastPickedTimeout = useRef<NodeJS.Timeout>()
  const dimensions = useWindowDimensions()
  const focused = useIsFocused()
  const [selectedToken, setSelectedToken] = useState<TokenType>()
  const lastRegion = useRef<Region>()
  const config = useConfig()
  const { business } = useBusinessSelector()
  const [mapBoundingBox, setMapBoundingBox] = useState<MapBoundingBox | undefined>()
  const [precision, setPrecision] = useState(PRECISION_DEFAULT)
  const [openModal, setOpenModal] = useState(false)
  const [openPostModal, setOpenPostModal] = useState(false)

  const { location } = useCurrentLocation({
    locationOptions: locationOptions
  })

  const { handleCenter, onGroupMarkerPress, onCenterChanged } = useMapActions({
    location,
    selectedToken,
    focused,
    setMapBoundingBox,
    setPrecision,
    //@ts-ignore
    mapRef
  })

  const onSelectToken = useThrottledCallback((token: TokenType) => {
    setOpenModal(true)
    setSelectedToken(token)
  }, 300)

  const onCloseModal = useThrottledCallback(() => {
    setOpenModal(false)
    setSelectedToken(undefined)
  }, 300)

  const onPostModal = useThrottledCallback(() => {
    setOpenModal(false)
    setOpenPostModal(true)
  }, 300)

  const onClosePostModal = useThrottledCallback(() => {
    setOpenPostModal(false)
    setSelectedToken(undefined)
    onPickUp()
  }, 300)

  const onPickUp = useThrottledCallback(() => {
    setLastPicked(selectedToken?.id)
    removeLastPickedTimeout.current = setTimeout(() => {
      setLastPicked(undefined)
    }, 5000)

    onCloseModal()
  }, 300)

  const onRegionChange = useThrottledCallback(
    (mapRegion: Region) => {
      if (pickingRef.current) return
      const boundingBox = mapRegion ? getBoundingBoxFromRegion(mapRegion) : null
      if (boundingBox && !selectedToken && focused) {
        setMapBoundingBox(boundingBox)
        lastRegion.current = mapRegion
        sdk.dataPool.region(RegionType.geopos, JSON.stringify(boundingBox))
        const precision = getPrecision(mapRegion.latitudeDelta) ?? PRECISION_DEFAULT
        setPrecision(precision)
      }
    },
    1000,
    { trailing: true, leading: false }
  )

  useEffect(() => {
    return () => {
      if (removeLastPickedTimeout.current) {
        clearTimeout(removeLastPickedTimeout.current)
      }
    }
  }, [])

  const initialRegion = useMemo(() => {
    if (!location) return
    const ASPECT_RATIO = dimensions.width / dimensions.height
    const LATITUDE_DELTA = 0.015
    const LONGITUDE_DELTA = LATITUDE_DELTA * ASPECT_RATIO

    return {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
      latitudeDelta: LATITUDE_DELTA,
      longitudeDelta: LONGITUDE_DELTA
    }
  }, [dimensions.height, dimensions.width, location])

  const initialCenter = useMemo(() => {
    if (!location) return

    return {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude
    }
  }, [location])

  const disableInitialRegion = useRef(false)

  useEffect(() => {
    if (!focused) return
    // We only create the initial map region once per mount (driven by user location).
    // That way next time the user navigates back to the map, it will not reset back to the initial region but it will use the last bounding box the user was looking at.
    // (unless the map unmounts and resets)
    if (initialRegion && !disableInitialRegion.current) {
      const boundingBox = initialRegion ? getBoundingBoxFromRegion(initialRegion) : null

      if (boundingBox) {
        onRegionChange(initialRegion)
        disableInitialRegion.current = true
      }
    } else if (lastRegion.current) {
      onRegionChange(lastRegion.current)
    }
  }, [initialRegion, focused, onRegionChange])

  const regions = sdk.dataPool.regions.filter(r => r.id === RegionType.geopos)

  const tokens = useMemo(() => regions[0]?.ARTokens ?? [], [regions])

  const filteredTokens = useMemo(() => {
    // remove duplicates
    const filtered = !business?.id
      ? tokens
      : tokens.filter(
          token =>
            token.studioInfo?.businessId === business.id ||
            (business.id === 'Cox1qh6ggb'
              ? (token as VatomTokenType).payload['vAtom::vAtomType'].publisher_fqdn === 'mx.joyapp'
              : false) // hack for joy's tokens
        )
    return filtered
  }, [business?.id, tokens])

  const discoverGroups = useGeoDiscoveryGroups(
    {
      filter: 'all',
      precision,
      ...mapBoundingBox
    },
    {
      select: result => ({
        groups: result.payload?.groups?.filter(group => group.count > 1) ?? [],
        singles: result.payload?.groups?.filter(group => group.count === 1) ?? []
      })
    }
  )

  const onMarkerClick = useCallback(
    (item: TokenType) => {
      if (!item.position) throw new Error('Token has no coordinates')
      if (config.features.maps.pickup?.enabled) onSelectToken(item)
    },
    [config.features.maps.pickup?.enabled, onSelectToken]
  )

  const markers = useMemo(() => {
    const extraMarkers = Platform.OS === 'web' ? createUserLocationMarkers(location) : []

    return !business?.id && precision <= 12
      ? [
          ...createGroupMarkers(discoverGroups?.data?.groups ?? [], onGroupMarkerPress),
          ...createSingleMarkers(
            discoverGroups?.data?.singles ?? [],
            filteredTokens,
            onMarkerClick
          ),
          ...extraMarkers
        ]
      : [...createCustomMarkers(filteredTokens, onMarkerClick), ...extraMarkers]
  }, [
    location,
    business?.id,
    precision,
    discoverGroups,
    onGroupMarkerPress,
    filteredTokens,
    onMarkerClick
  ])

  return {
    location,
    selectedToken,
    onPickUp,
    onCloseModal,
    openModal,
    onRegionChange,
    markers,
    handleCenter,

    // only used for native
    initialRegion,

    // only used for web
    initialCenter,
    onCenterChanged,
    openPostModal,
    onPostModal,
    onClosePostModal
  }
}
